/* ============================================================================
   ROWMAN — cart
   Replaces the prototype's localStorage cart with Shopify's Cart AJAX API.
   Drawer and cart page share one render path, so the clinical intake manifest
   layout is identical in both. All prices come from Shopify, never the DOM.
   ============================================================================ */
(function () {
  'use strict';

  var R = window.ROWMAN;
  if (!R) return;

  var routes = R.routes || {};
  var strings = R.strings || {};

  /* ───────────────────────── API ───────────────────────── */

  function req(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(body || {})
    }).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok) {
          var err = new Error(data.description || data.message || strings.cartError);
          err.payload = data;
          throw err;
        }
        return data;
      });
    });
  }

  var Cart = {
    get: function () {
      return fetch((routes.cartUrl || '/cart') + '.js', {
        headers: { 'Accept': 'application/json' }
      }).then(function (r) { return r.json(); });
    },
    add: function (items) {
      return req(routes.cartAddUrl || '/cart/add.js', { items: items });
    },
    change: function (key, quantity) {
      return req(routes.cartChangeUrl || '/cart/change.js', { id: key, quantity: quantity });
    },
    clear: function () {
      return req(routes.cartClearUrl || '/cart/clear.js', {});
    }
  };
  R.Cart = Cart;

  /* ───────────────────────── shared render ───────────────────────── */

  function setBusy(on) {
    document.querySelectorAll('[data-cart-root]').forEach(function (el) {
      el.classList.toggle('is-busy', !!on);
      el.setAttribute('aria-busy', on ? 'true' : 'false');
    });
  }

  function updateBubbles(count) {
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = count;
      el.setAttribute('data-cart-count', count);
    });
  }

  /**
   * Re-render every cart surface on the page by fetching the cart sections
   * from Shopify. Using Section Rendering keeps markup authored in Liquid,
   * so the drawer and page never drift from the approved design.
   */
  function refresh(openDrawer) {
    var ids = [];
    document.querySelectorAll('[data-cart-section]').forEach(function (el) {
      var id = el.getAttribute('data-cart-section');
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });

    var url = (routes.cartUrl || '/cart') + '?sections=' + ids.join(',');

    return fetch(url, { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (sections) {
        ids.forEach(function (id) {
          if (!sections[id]) return;
          var doc = new DOMParser().parseFromString(sections[id], 'text/html');
          var fresh = doc.querySelector('[data-cart-section="' + id + '"]');
          document.querySelectorAll('[data-cart-section="' + id + '"]').forEach(function (target) {
            if (!fresh) return;
            target.innerHTML = fresh.innerHTML;
            R.mountAll(target);
          });
        });
        return Cart.get();
      })
      .then(function (cart) {
        updateBubbles(cart.item_count);
        R.announce(
          cart.item_count === 0
            ? (strings.cartEmpty || 'Cart is empty')
            : (strings.cartUpdated || 'Cart updated') + ' — ' +
              cart.item_count + ' ' +
              (cart.item_count === 1 ? (strings.item || 'item') : (strings.items || 'items'))
        );
        if (openDrawer) openCartDrawer();
        document.dispatchEvent(new CustomEvent('rowman:cart:updated', { detail: cart }));
        return cart;
      })
      .catch(function (err) {
        console.error('[ROWMAN] cart refresh failed', err);
      });
  }
  R.refreshCart = refresh;

  function openCartDrawer() {
    var drawer = document.querySelector('[data-cart-drawer]');
    if (drawer && drawer.rowmanOpen) drawer.rowmanOpen();
  }
  R.openCartDrawer = openCartDrawer;

  function showError(scope, message) {
    var box = (scope || document).querySelector('[data-cart-error]');
    if (!box) { R.toast(message); return; }
    box.textContent = message;
    box.hidden = false;
    box.classList.add('is-error');
    setTimeout(function () { box.hidden = true; }, 6000);
  }

  /* ═══════════════════ MODULE: cart drawer ═══════════════════ */

  R.register({
    name: 'CartDrawer',
    scope: '[data-cart-drawer]',
    mount: function (drawer) {
      var overlay = document.querySelector('[data-cart-overlay]');
      var openers = Array.prototype.slice.call(document.querySelectorAll('[data-cart-open]'));
      var closeBtn = drawer.querySelector('[data-cart-close]');
      var lastFocus = null;
      var releaseTrap = null;

      function open(e) {
        if (e) { e.preventDefault(); e.stopPropagation(); }
        lastFocus = document.activeElement;
        drawer.classList.add('is-open');
        drawer.setAttribute('aria-hidden', 'false');
        if (overlay) {
          overlay.classList.add('is-open');
          overlay.setAttribute('aria-hidden', 'false');
        }
        document.body.classList.add('cart-open');
        openers.forEach(function (o) { o.setAttribute('aria-expanded', 'true'); });
        releaseTrap = R.trapFocus(drawer);
        var f = drawer.querySelector(R.FOCUSABLE);
        if (f) f.focus();
      }
      function close() {
        drawer.classList.remove('is-open');
        drawer.setAttribute('aria-hidden', 'true');
        if (overlay) {
          overlay.classList.remove('is-open');
          overlay.setAttribute('aria-hidden', 'true');
        }
        document.body.classList.remove('cart-open');
        openers.forEach(function (o) { o.setAttribute('aria-expanded', 'false'); });
        if (releaseTrap) { releaseTrap(); releaseTrap = null; }
        if (lastFocus && lastFocus.focus) lastFocus.focus();
      }
      function onKey(e) {
        if (e.key === 'Escape' && drawer.classList.contains('is-open')) close();
      }

      drawer.rowmanOpen = open;

      // Delegated so a Theme Editor header reload can never orphan the binding
      function onDocClick(e) {
        var trigger = e.target.closest('[data-cart-open]');
        if (trigger) open(e);
      }
      document.addEventListener('click', onDocClick);
      openers.forEach(function (o) { o.setAttribute('aria-expanded', 'false'); });
      if (closeBtn) closeBtn.addEventListener('click', close);
      if (overlay) overlay.addEventListener('click', close);
      document.addEventListener('keydown', onKey);

      return function () {
        document.removeEventListener('click', onDocClick);
        if (closeBtn) closeBtn.removeEventListener('click', close);
        if (overlay) overlay.removeEventListener('click', close);
        document.removeEventListener('keydown', onKey);
        document.body.classList.remove('cart-open');
        delete drawer.rowmanOpen;
      };
    }
  });

  /* ═══════════════════ MODULE: cart line controls ═══════════════════
     Bound at document level via delegation so it survives every section
     re-render without rebinding. */

  R.register({
    name: 'CartLines',
    scope: null,
    mount: function () {
      function onClick(e) {
        var btn = e.target.closest('[data-cart-action]');
        if (!btn) return;
        e.preventDefault();

        var action = btn.getAttribute('data-cart-action');
        var line = btn.closest('[data-line-key]');
        if (!line) return;
        var key = line.getAttribute('data-line-key');
        var qty = parseInt(line.getAttribute('data-line-qty') || '1', 10);

        var next;
        if (action === 'increase') next = Math.min(99, qty + 1);
        else if (action === 'decrease') next = qty - 1;
        else if (action === 'remove') next = 0;
        else return;

        setBusy(true);
        Cart.change(key, next)
          .then(function () { return refresh(false); })
          .catch(function (err) { showError(line.closest('[data-cart-root]'), err.message); })
          .then(function () { setBusy(false); });
      }

      function onChange(e) {
        var input = e.target.closest('[data-cart-qty-input]');
        if (!input) return;
        var line = input.closest('[data-line-key]');
        if (!line) return;
        var key = line.getAttribute('data-line-key');
        var next = Math.max(0, Math.min(99, parseInt(input.value, 10) || 0));

        setBusy(true);
        Cart.change(key, next)
          .then(function () { return refresh(false); })
          .catch(function (err) { showError(line.closest('[data-cart-root]'), err.message); })
          .then(function () { setBusy(false); });
      }

      document.addEventListener('click', onClick);
      document.addEventListener('change', onChange);
      return function () {
        document.removeEventListener('click', onClick);
        document.removeEventListener('change', onChange);
      };
    }
  });

  /* ═══════════════════ MODULE: add to cart forms ═══════════════════ */

  R.register({
    name: 'AddToCart',
    scope: '[data-product-form]',
    mount: function (form) {
      function onSubmit(e) {
        e.preventDefault();
        var btn = form.querySelector('[type="submit"]');
        var idInput = form.querySelector('[name="id"]');
        if (!idInput || !idInput.value) {
          showError(form, strings.selectVariant || 'Select an option');
          return;
        }
        var qtyInput = form.querySelector('[name="quantity"]');
        var item = {
          id: parseInt(idInput.value, 10),
          quantity: parseInt((qtyInput && qtyInput.value) || '1', 10)
        };

        // line item properties, if the section renders any
        var props = {};
        form.querySelectorAll('[name^="properties["]').forEach(function (p) {
          var name = p.getAttribute('name').replace(/^properties\[|\]$/g, '');
          if (p.value) props[name] = p.value;
        });
        if (Object.keys(props).length) item.properties = props;

        if (btn) { btn.classList.add('rw-btn-loading'); btn.disabled = true; }

        Cart.add([item])
          .then(function () { return refresh(R.cartType === 'drawer'); })
          .then(function () { R.toast(strings.addedToCart || 'Added to cart'); })
          .catch(function (err) { showError(form, err.message); })
          .then(function () {
            if (btn) { btn.classList.remove('rw-btn-loading'); btn.disabled = false; }
          });
      }
      form.addEventListener('submit', onSubmit);
      return function () { form.removeEventListener('submit', onSubmit); };
    }
  });

  /* ═══════════════════ MODULE: quick-add on product cards ═══════════════════
     Preserves the prototype's hover dropdown. Options and availability come
     from the Shopify variant data rendered into the card by Liquid. */

  R.register({
    name: 'QuickAdd',
    scope: '[data-quick-add]',
    mount: function (root) {
      var trigger = root.querySelector('[data-quick-add-trigger]');
      var panel = root.querySelector('[data-quick-add-panel]');
      if (!trigger || !panel) return;

      function open(e) {
        e.preventDefault();
        e.stopPropagation();
        document.querySelectorAll('[data-quick-add-panel].is-open').forEach(function (p) {
          if (p !== panel) {
            p.classList.remove('is-open');
            p.setAttribute('aria-hidden', 'true');
          }
        });
        var isOpen = panel.classList.toggle('is-open');
        panel.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
        trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      }

      function onPick(e) {
        var btn = e.target.closest('[data-variant-id]');
        if (!btn) return;
        e.preventDefault();
        e.stopPropagation();
        if (btn.hasAttribute('disabled')) return;

        btn.classList.add('rw-btn-loading');
        Cart.add([{ id: parseInt(btn.getAttribute('data-variant-id'), 10), quantity: 1 }])
          .then(function () { return refresh(R.cartType === 'drawer'); })
          .then(function () {
            R.toast(strings.addedToCart || 'Added to cart');
            panel.classList.remove('is-open');
            panel.setAttribute('aria-hidden', 'true');
            trigger.setAttribute('aria-expanded', 'false');
          })
          .catch(function (err) { showError(root, err.message); })
          .then(function () { btn.classList.remove('rw-btn-loading'); });
      }

      function onOutside(e) {
        if (!root.contains(e.target)) {
          panel.classList.remove('is-open');
          panel.setAttribute('aria-hidden', 'true');
          trigger.setAttribute('aria-expanded', 'false');
        }
      }
      function onKey(e) {
        if (e.key === 'Escape') {
          panel.classList.remove('is-open');
          panel.setAttribute('aria-hidden', 'true');
          trigger.setAttribute('aria-expanded', 'false');
        }
      }

      trigger.addEventListener('click', open);
      panel.addEventListener('click', onPick);
      document.addEventListener('click', onOutside);
      document.addEventListener('keydown', onKey);

      return function () {
        trigger.removeEventListener('click', open);
        panel.removeEventListener('click', onPick);
        document.removeEventListener('click', onOutside);
        document.removeEventListener('keydown', onKey);
      };
    }
  });

  /* ═══════════════════ MODULE: pre-checkout review handoff ═══════════════════
     §9.1 option B. The theme never collects payment data. This button posts
     the customer into Shopify's hosted checkout. */

  R.register({
    name: 'CheckoutHandoff',
    scope: '[data-checkout-handoff]',
    mount: function (btn) {
      function go(e) {
        e.preventDefault();
        if (btn.getAttribute('aria-disabled') === 'true') return;
        btn.classList.add('rw-btn-loading');
        R.announce(strings.redirectingToCheckout || 'Redirecting to secure checkout');
        window.location.href = routes.cartUrl ? routes.cartUrl + '/checkout' : '/checkout';
      }
      btn.addEventListener('click', go);
      return function () { btn.removeEventListener('click', go); };
    }
  });

  /* ═══════════════════ MODULE: shipping country note ═══════════════════
     Informational only — real rates are calculated by Shopify at checkout. */

  R.register({
    name: 'ShippingNote',
    scope: '[data-shipping-country]',
    mount: function (select) {
      var note = document.querySelector('[data-shipping-note]');
      function onChange() {
        if (!note) return;
        var opt = select.options[select.selectedIndex];
        note.textContent = (strings.shippingCalculated ||
          'Rates and duties for {country} are calculated at checkout.')
          .replace('{country}', opt ? opt.textContent.trim() : '');
      }
      select.addEventListener('change', onChange);
      onChange();
      return function () { select.removeEventListener('change', onChange); };
    }
  });

}());
